/**
 * Admin Knowledge Base Management
 * Manage categories and articles
 */

import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Folder,
  Search,
  Filter,
} from 'lucide-react';

interface Category {
  id: number;
  name: string;
  description: string | null;
  article_count: number;
  is_visible: number;
}

interface Article {
  id: number;
  category_id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  status: 'draft' | 'published';
  visibility: 'public' | 'clients_only';
  views: number;
  helpful_votes: number;
  not_helpful_votes: number;
  published_at: string | null;
  created_at: string;
}

export const KnowledgeBase: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'articles' | 'categories'>('articles');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Load data
  useEffect(() => {
    loadCategories();
    loadArticles();
  }, [filterCategory, filterStatus]);

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/v1/admin/knowledge-base/categories?includeHidden=true', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (filterCategory) params.append('category_id', filterCategory.toString());
      if (filterStatus) params.append('status', filterStatus);

      const response = await fetch(`/api/v1/admin/knowledge-base/articles?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setArticles(data.data.articles);
      }
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadArticles();
  };

  const handleDeleteArticle = async (articleId: number) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      const response = await fetch(`/api/v1/admin/knowledge-base/articles/${articleId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadArticles();
        loadCategories(); // Refresh counts
      }
    } catch (error) {
      console.error('Failed to delete article:', error);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!confirm('Are you sure you want to delete this category? All articles in this category will also be deleted.')) return;

    try {
      const response = await fetch(`/api/v1/admin/knowledge-base/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadCategories();
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  const getVotePercentage = (article: Article) => {
    const total = article.helpful_votes + article.not_helpful_votes;
    if (total === 0) return 0;
    return Math.round((article.helpful_votes / total) * 100);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              Knowledge Base
            </h1>
            <p className="text-slate-400 mt-1">
              Manage help articles and categories
            </p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New {selectedTab === 'articles' ? 'Article' : 'Category'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700/50">
        <button
          onClick={() => setSelectedTab('articles')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'articles'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Articles ({articles.length})
        </button>
        <button
          onClick={() => setSelectedTab('categories')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'categories'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Categories ({categories.length})
        </button>
      </div>

      {/* Articles Tab */}
      {selectedTab === 'articles' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search articles..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
                  />
                </div>
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value ? parseInt(e.target.value) : '')}
                className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
              >
                <option value="">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
              <button
                type="submit"
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </form>
          </div>

          {/* Articles Table */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Title</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Category</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Visibility</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Views</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Helpful</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : articles.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-400">
                      No articles found
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => {
                    const category = categories.find((c) => c.id === article.category_id);
                    const helpfulPercentage = getVotePercentage(article);

                    return (
                      <tr key={article.id} className="hover:bg-slate-700/30">
                        <td className="p-4">
                          <div>
                            <div className="text-white font-medium">{article.title}</div>
                            {article.excerpt && (
                              <div className="text-sm text-slate-400 mt-1 line-clamp-1">
                                {article.excerpt}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-sm">
                            {category?.name || 'Unknown'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2 py-1 rounded text-sm ${
                              article.status === 'published'
                                ? 'bg-green-900 text-green-300'
                                : 'bg-yellow-900 text-yellow-300'
                            }`}
                          >
                            {article.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-300 text-sm">
                            {article.visibility === 'public' ? 'Public' : 'Clients Only'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1 text-slate-300">
                            <Eye className="w-4 h-4" />
                            {article.views}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 text-green-400">
                              <ThumbsUp className="w-4 h-4" />
                              {article.helpful_votes}
                            </div>
                            <div className="flex items-center gap-1 text-red-400">
                              <ThumbsDown className="w-4 h-4" />
                              {article.not_helpful_votes}
                            </div>
                            <span className="text-sm text-slate-400">
                              ({helpfulPercentage}%)
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button
                              className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteArticle(article.id)}
                              className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Categories Tab */}
      {selectedTab === 'categories' && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Name</th>
                <th className="text-left p-4 text-slate-300 font-medium">Description</th>
                <th className="text-left p-4 text-slate-300 font-medium">Articles</th>
                <th className="text-left p-4 text-slate-300 font-medium">Visible</th>
                <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-400">
                    No categories found
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-white font-medium">
                        <Folder className="w-4 h-4 text-orange-400" />
                        {category.name}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400">
                        {category.description || '-'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300">{category.article_count}</span>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded text-sm ${
                          category.is_visible
                            ? 'bg-green-900 text-green-300'
                            : 'bg-slate-700/50 text-slate-400'
                        }`}
                      >
                        {category.is_visible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
